import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { Spinner } from '../components/ui/Spinner.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { financeApi } from '../services/financeApi.js';
import { downloadEbicsLetterPdf } from '../utils/pdf.js';

const EMPTY = {
  bankName: '',
  hostId: '',
  partnerId: '',
  userId: '',
  ebicsUrl: '',
  ebicsVersion: 'H004',
  accountIban: '',
};

const STATUS_VARIANT = {
  NEW: 'neutral',
  INI_SENT: 'info',
  HIA_SENT: 'info',
  READY: 'success',
  ERROR: 'danger',
};

function todayMinusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function EbicsSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(false);
  const [hashes, setHashes] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [range, setRange] = useState({ from: todayMinusDays(30), to: todayMinusDays(0) });
  const [downloadResult, setDownloadResult] = useState(null);

  const status = useQuery({
    queryKey: ['finance', 'ebics-status'],
    queryFn: () => financeApi.getEbicsStatus().then((r) => r.items),
  });

  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
  });

  useEffect(() => {
    if (status.data) {
      setForm((prev) => ({
        ...prev,
        bankName: status.data.bankName ?? '',
        hostId: status.data.hostId ?? '',
        partnerId: status.data.partnerId ?? '',
        userId: status.data.userId ?? '',
        ebicsUrl: status.data.ebicsUrl ?? '',
        ebicsVersion: status.data.ebicsVersion ?? 'H004',
        accountIban: status.data.accountIban ?? '',
      }));
    }
  }, [status.data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['finance', 'ebics-status'] });

  const save = useMutation({
    mutationFn: (body) => financeApi.saveEbicsConfig(body),
    onSuccess: () => {
      refresh();
      setSaved(true);
    },
  });

  const initKeys = useMutation({
    mutationFn: () => financeApi.ebicsInitKeys(),
    onSuccess: (data) => {
      setHashes(data?.hashes ?? null);
      refresh();
    },
  });

  const sendIni = useMutation({ mutationFn: () => financeApi.ebicsSendIni(), onSuccess: refresh });
  const sendHia = useMutation({ mutationFn: () => financeApi.ebicsSendHia(), onSuccess: refresh });
  const fetchBankKeys = useMutation({ mutationFn: () => financeApi.ebicsFetchBankKeys(), onSuccess: refresh });
  const download = useMutation({
    mutationFn: (body) => financeApi.ebicsDownload(body),
    onSuccess: (data) => {
      setDownloadResult(data);
      queryClient.invalidateQueries({ queryKey: ['finance', 'bank-transactions'] });
    },
  });

  const set = (key) => (e) => {
    setSaved(false);
    setForm((p) => ({ ...p, [key]: e.target.value }));
  };

  function submit(e) {
    e.preventDefault();
    save.mutate(form);
  }

  function printLetter() {
    if (!hashes) {
      return;
    }
    downloadEbicsLetterPdf({
      ids: {
        bankName: form.bankName,
        hostId: form.hostId,
        partnerId: form.partnerId,
        userId: form.userId,
      },
      hashes,
      settings: settings.data,
      t,
    });
  }

  if (status.isLoading) {
    return <Card><Spinner /></Card>;
  }

  const s = status.data || {};
  const keysGenerated = Boolean(s.keysGenerated);
  const isReady = s.status === 'READY';
  const masterKeyMissing = s.masterKeyConfigured === false;
  const anyError =
    save.error || initKeys.error || sendIni.error || sendHia.error || fetchBankKeys.error;

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="alert warning" role="note">
          {t('ebics.experimental')}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span className="field-label" style={{ margin: 0 }}>{t('ebics.statusLabel')}</span>
          <Badge variant={STATUS_VARIANT[s.status] || 'neutral'}>
            {t(`ebics.status.${s.status}`) || s.status}
          </Badge>
        </div>

        {masterKeyMissing && <div className="alert danger">{t('ebics.masterKeyMissing')}</div>}
        {s.lastError ? <div className="alert danger">{s.lastError}</div> : null}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <label className="field">
            <span className="field-label">{t('ebics.bankName')}</span>
            <input className="input" value={form.bankName} onChange={set('bankName')} placeholder="UBS / PostFinance / …" />
          </label>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('ebics.hostId')}</span>
              <input className="input" value={form.hostId} onChange={set('hostId')} autoComplete="off" />
            </label>
            <label className="field">
              <span className="field-label">{t('ebics.partnerId')}</span>
              <input className="input" value={form.partnerId} onChange={set('partnerId')} autoComplete="off" />
            </label>
          </div>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('ebics.userId')}</span>
              <input className="input" value={form.userId} onChange={set('userId')} autoComplete="off" />
            </label>
            <label className="field">
              <span className="field-label">{t('ebics.version')}</span>
              <select className="input" value={form.ebicsVersion} onChange={set('ebicsVersion')}>
                <option value="H004">H004</option>
                <option value="H005">H005</option>
              </select>
            </label>
          </div>
          <label className="field">
            <span className="field-label">{t('ebics.url')}</span>
            <input className="input" value={form.ebicsUrl} onChange={set('ebicsUrl')} placeholder="https://ebics.bank.ch/ebics" />
          </label>
          <label className="field">
            <span className="field-label">{t('ebics.accountIban')}</span>
            <input className="input" value={form.accountIban} onChange={set('accountIban')} placeholder="CH00 0000 0000 0000 0000 0" />
          </label>

          {anyError && <div className="alert danger">{anyError.message || t('common.loadError')}</div>}
          {saved && !save.isPending && <div className="alert success">{t('ebics.saved')}</div>}

          <div className="page-actions">
            <Button variant="primary" onClick={submit} disabled={save.isPending}>
              {save.isPending ? `${t('common.save')}…` : t('common.save')}
            </Button>
          </div>
        </form>

        <div className="settings-divider">{t('ebics.workflow')}</div>
        <p className="muted text-sm" style={{ marginTop: 0 }}>{t('ebics.workflow.intro')}</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          <Button
            variant="secondary"
            onClick={() => initKeys.mutate()}
            disabled={initKeys.isPending || masterKeyMissing}
          >
            {initKeys.isPending ? `${t('ebics.btn.initKeys')}…` : t('ebics.btn.initKeys')}
          </Button>
          <Button variant="secondary" onClick={printLetter} disabled={!hashes && !keysGenerated}>
            {t('ebics.btn.letter')}
          </Button>
          <Button variant="secondary" onClick={() => sendIni.mutate()} disabled={!keysGenerated || sendIni.isPending}>
            {sendIni.isPending ? `${t('ebics.btn.ini')}…` : t('ebics.btn.ini')}
          </Button>
          <Button variant="secondary" onClick={() => sendHia.mutate()} disabled={!keysGenerated || sendHia.isPending}>
            {sendHia.isPending ? `${t('ebics.btn.hia')}…` : t('ebics.btn.hia')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => fetchBankKeys.mutate()}
            disabled={!keysGenerated || fetchBankKeys.isPending}
          >
            {fetchBankKeys.isPending ? `${t('ebics.btn.hpb')}…` : t('ebics.btn.hpb')}
          </Button>
          <Button variant="primary" onClick={() => { setDownloadResult(null); setDownloadOpen(true); }} disabled={!isReady}>
            {t('ebics.btn.download')}
          </Button>
        </div>

        {keysGenerated && !hashes && (
          <p className="muted text-sm">{t('ebics.letterHint')}</p>
        )}
      </div>

      <Modal
        open={downloadOpen}
        title={t('ebics.download.title')}
        onClose={() => setDownloadOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDownloadOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={() => download.mutate(range)}
              disabled={download.isPending}
            >
              {download.isPending ? `${t('ebics.btn.download')}…` : t('ebics.btn.download')}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('ebics.download.from')}</span>
              <input
                className="input"
                type="date"
                value={range.from}
                onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))}
              />
            </label>
            <label className="field">
              <span className="field-label">{t('ebics.download.to')}</span>
              <input
                className="input"
                type="date"
                value={range.to}
                onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))}
              />
            </label>
          </div>
          {download.isError && (
            <div className="alert danger">{download.error?.message || t('common.loadError')}</div>
          )}
          {downloadResult && (
            <div className="alert success">
              {t('ebics.download.imported').replace('{n}', String(downloadResult.imported ?? 0))}
              {downloadResult.note ? ` ${downloadResult.note}` : ''}
              {' '}
              <Link to="/finance/bank" onClick={() => setDownloadOpen(false)}>
                {t('ebics.download.goToBank')}
              </Link>
            </div>
          )}
        </div>
      </Modal>
    </Card>
  );
}
